import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Plus, Search, Edit, Trash2, MapPin, Phone, Mail, Building2,
  Users, RefreshCw, Eye, Filter, Download, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label, Select, Textarea } from '../../components/ui/Form';
import { Badge } from '../../components/ui/Badge';
import Pagination from '../../components/ui/Pagination';
import { useToast } from '../../contexts/ToastContext';
import { branchesAPI, lookupDataAPI } from '../../services/api';

const branchSchema = z.object({
  branch_name: z.string().min(1, 'Branch name is required'),
  branch_code: z.string().min(1, 'Branch code is required'),
  address: z.string().min(1, 'Address is required'),
  contact_person: z.string().min(1, 'Contact person is required'),
  contact_email: z.string().email('Invalid email address'),
  contact_phone: z.string().min(1, 'Contact phone is required'),
});

const BranchesManagement = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [viewingBranch, setViewingBranch] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, branchId: null, branchName: '' });
  
  // Ref guards to prevent duplicate API calls
  const lastQueryRef = useRef({ page: null, search: null });
  const hasLoadedStats = useRef(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [loadingPage, setLoadingPage] = useState(false);
  
  // Dashboard stats state
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    totalSchools: 0,
    totalActiveBranches: 0,
  });

  const defaultValues = {
    branch_name: '',
    branch_code: '',
    address: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
  };

  // Simple data loading function
  const loadData = async (page = 1, search = '') => {
    try {
      setLoading(true);
      console.log('🌐 Making API calls...');
      const branchesResponse = await branchesAPI.getBranches({ page, limit: pagination.itemsPerPage, search });
      console.log('✅ API Response received:', { branchesResponse });
      
      // Map branches data - try different structures
      let mappedBranches = [];
      if (Array.isArray(branchesResponse)) {
        mappedBranches = branchesResponse;
      } else if (branchesResponse?.data?.branches) {
        mappedBranches = branchesResponse.data.branches;
      } else if (branchesResponse?.branches) {
        mappedBranches = branchesResponse.branches;
      } else if (branchesResponse?.data) {
        mappedBranches = Array.isArray(branchesResponse.data) ? branchesResponse.data : [];
      }
      
      console.log('📊 Mapped branches:', mappedBranches);
      setBranches(mappedBranches);
      
      // Update pagination
      if (branchesResponse?.data?.page) {
        setPagination(prev => ({
          ...prev,
          currentPage: branchesResponse.data.page,
          totalPages: branchesResponse.data.total_pages || 1,
          totalItems: branchesResponse.data.total || mappedBranches.length,
        }));
      } else {
        setPagination(prev => ({
          ...prev,
          currentPage: 1,
          totalPages: 1,
          totalItems: mappedBranches.length,
        }));
      }
      
    } catch (error) {
      console.error('❌ Failed to load data:', error);
      toast.error('Failed to load data', error.message);
    } finally {
      setLoading(false);
      setLoadingPage(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsResponse = await lookupDataAPI.getDashboardStats();
      const mappedStats = {
        totalUsers: statsResponse?.total_users || statsResponse?.totalUsers || 0,
        totalSchools: statsResponse?.total_schools || statsResponse?.totalSchools || 0,
        totalActiveBranches: statsResponse?.total_active_branches || statsResponse?.totalActiveBranches || 0,
      };
      console.log('📊 Mapped stats:', mappedStats);
      setDashboardStats(mappedStats);
    } catch (error) {
      console.error('❌ Failed to load dashboard stats:', error);
      toast.error('Failed to load dashboard stats', error.message);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 450);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (hasLoadedStats.current) return;
    loadStats();
    hasLoadedStats.current = true;
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm !== lastQueryRef.current.search && pagination.currentPage !== 1) {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      return;
    }

    const nextQuery = { page: pagination.currentPage, search: debouncedSearchTerm };
    if (lastQueryRef.current.page === nextQuery.page && lastQueryRef.current.search === nextQuery.search) {
      return;
    }

    lastQueryRef.current = nextQuery;
    loadData(nextQuery.page, nextQuery.search);
  }, [pagination.currentPage, debouncedSearchTerm]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(branchSchema),
    defaultValues,
  });

  const refreshData = async () => {
    setRefreshing(true);
    try {
      console.log('🔄 Manual refresh triggered');
      await loadData(pagination.currentPage, debouncedSearchTerm);
      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddNew = () => {
    setEditingBranch(null);
    reset(defaultValues);
    setShowForm(true);
  };

  const handlePageChange = async (page) => {
    setLoadingPage(true);
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (editingBranch) {
        await branchesAPI.updateBranch(editingBranch.id, data);
        toast.success('Branch updated successfully!');
      } else {
        await branchesAPI.createBranch(data);
        toast.success('Branch created successfully!');
      }
      
      setShowForm(false);
      setEditingBranch(null);
      reset();
      
      // Reload data
      await loadData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save branch';
      toast.error('Failed to save branch', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    reset({
      branch_name: branch.branch_name,
      branch_code: branch.branch_code,
      address: branch.address,
      contact_person: branch.contact_person,
      contact_email: branch.contact_email,
      contact_phone: branch.contact_phone,
    });
    setShowForm(true);
  };

  const handleDelete = (branch) => {
    setDeleteConfirm({ show: true, branchId: branch.id, branchName: branch.branch_name });
  };

  const confirmDelete = async () => {
    try {
      await branchesAPI.deleteBranch(deleteConfirm.branchId);
      toast.success('Branch deleted successfully!');
      
      // Reload data
      await loadData();
      
      setDeleteConfirm({ show: false, branchId: null, branchName: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete branch';
      toast.error('Failed to delete branch', errorMessage);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, branchId: null, branchName: '' });
  };

  const handleView = (branch) => {
    setViewingBranch(branch);
  };

  // Remove client-side filtering since it's handled by backend API
  const filteredBranches = branches;

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Branches Management</h1>
            <p className="text-gray-600 mt-2">Manage organization branches and locations</p>
            {!loading && branches.length > 0 && (
              <p className="text-xs text-green-600 mt-1 flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                Data loaded successfully
              </p>
            )}
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <Button onClick={handleAddNew}>
              <Plus size={16} className="mr-2" />
              Add Branch
            </Button>
            <Button variant="outline" size="sm" onClick={refreshData} disabled={refreshing || loading}>
              <RefreshCw size={16} className={`mr-2 ${refreshing || loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-600">{pagination.totalItems}</p>
                  <p className="text-sm text-gray-600">Total Branches</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="text-blue-600" size={20} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-green-600">
                    {dashboardStats.totalActiveBranches}
                  </p>
                  <p className="text-sm text-gray-600">Active Branches</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Users className="text-green-600" size={20} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-purple-600">
                    {dashboardStats.totalSchools}
                  </p>
                  <p className="text-sm text-gray-600">Total Schools</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Building2 className="text-purple-600" size={20} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-orange-600">
                    {dashboardStats.totalUsers}
                  </p>
                  <p className="text-sm text-gray-600">Total Users</p>
                </div>
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Users className="text-orange-600" size={20} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Branch Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1"
          >
            <Card>
              <CardHeader>
                <CardTitle>{editingBranch ? 'Edit Branch' : 'Add New Branch'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <Label htmlFor="branch_name">Branch Name</Label>
                    <Controller
                      name="branch_name"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Enter branch name"
                          {...field}
                          error={errors.branch_name?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="branch_code">Branch Code</Label>
                    <Controller
                      name="branch_code"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Enter branch code"
                          {...field}
                          error={errors.branch_code?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="address">Address</Label>
                    <Controller
                      name="address"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          placeholder="Enter branch address"
                          {...field}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_person">Contact Person</Label>
                    <Controller
                      name="contact_person"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Enter contact person name"
                          {...field}
                          error={errors.contact_person?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_email">Contact Email</Label>
                    <Controller
                      name="contact_email"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="email"
                          placeholder="Enter contact email"
                          {...field}
                          error={errors.contact_email?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_phone">Contact Phone</Label>
                    <Controller
                      name="contact_phone"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Enter contact phone"
                          {...field}
                          error={errors.contact_phone?.message}
                        />
                      )}
                    />
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      type="submit"
                      className="flex-1"
                      loading={loading}
                    >
                      {editingBranch ? 'Update' : 'Create'} Branch
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForm(false);
                        setEditingBranch(null);
                        reset();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Branches List */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className={`${showForm ? 'lg:col-span-2' : 'lg:col-span-3'}`}
        >
          <Card>
            <CardHeader>
              <CardTitle>Branches</CardTitle>
              <div className="flex items-center space-x-3 mt-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search branches..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
                <Button variant="outline" size="sm">
                  <Download size={16} className="mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {(loading || loadingPage) && branches.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-2"></div>
                    <p className="text-gray-500">Loading branches...</p>
                    {loading && <p className="text-xs text-gray-400 mt-1">Fetching from API...</p>}
                  </div>
                ) : branches.length === 0 && !loading ? (
                  <div className="text-center py-8">
                    <Building2 className="text-gray-400 mx-auto mb-2" size={48} />
                    <p className="text-gray-500">No branches found</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={refreshData}
                      className="mt-2"
                      disabled={refreshing}
                    >
                      <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                      Retry
                    </Button>
                  </div>
                ) : (
                  filteredBranches.map((branch, index) => (
                    <motion.div
                      key={branch.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                      className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-gray-900">{branch.branch_name}</h3>
                            <Badge variant={branch.is_active ? 'success' : 'error'}>
                              {branch.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                            <div className="flex items-center">
                              <MapPin size={14} className="mr-1" />
                              {branch.address}
                            </div>
                            <div className="flex items-center">
                              <Users size={14} className="mr-1" />
                              {branch.school_count || 0} schools
                            </div>
                            <div className="flex items-center">
                              <Phone size={14} className="mr-1" />
                              {branch.contact_phone}
                            </div>
                            <div className="flex items-center">
                              <Mail size={14} className="mr-1" />
                              {branch.contact_email}
                            </div>
                          </div>

                          <div className="mt-2 text-xs text-gray-500">
                            Contact: {branch.contact_person} • Code: {branch.branch_code}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 ml-4">
                          <Button size="sm" variant="ghost" onClick={() => handleView(branch)}>
                            <Eye size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(branch)}>
                            <Edit size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(branch)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
              
              {/* Pagination Component */}
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                itemsPerPage={pagination.itemsPerPage}
                onPageChange={handlePageChange}
                loading={loadingPage}
              />
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Branch Details Modal */}
      {viewingBranch && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setViewingBranch(null)}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">{viewingBranch.branch_name}</h2>
              <Button variant="ghost" size="sm" onClick={() => setViewingBranch(null)}>
                ×
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Branch Code</p>
                <p className="font-medium">{viewingBranch.branch_code}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <Badge variant={viewingBranch.is_active ? 'success' : 'error'}>
                  {viewingBranch.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500">Address</p>
                <p className="font-medium">{viewingBranch.address}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Schools</p>
                <p className="font-medium">{viewingBranch.school_count || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Contact Person</p>
                <p className="font-medium">{viewingBranch.contact_person}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Contact Phone</p>
                <p className="font-medium">{viewingBranch.contact_phone}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Contact Email</p>
                <p className="font-medium">{viewingBranch.contact_email}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={cancelDelete}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center mb-4">
              <AlertTriangle className="text-red-600 mr-3" size={24} />
              <h3 className="text-lg font-bold text-gray-900">Delete Branch</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to delete the branch <strong>"{deleteConfirm.branchName}"</strong>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This action cannot be undone and will also delete all associated schools and data.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={cancelDelete}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Delete Branch
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default BranchesManagement;
